import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Mail, 
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

type VerificationStatus = 'loading' | 'success' | 'error' | 'invalid';

export default function VerifyEmailPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<VerificationStatus>('loading');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isResending, setIsResending] = useState(false);

  const [searchParams] = useSearchParams();
  const userEmail = searchParams.get('email');

  useEffect(() => {
    if (userEmail) {
      setEmail(userEmail);
    }

    if (!token) {
      setStatus('invalid');
      setMessage('Invalid verification link. Please request a new one.');
      return;
    }

    verifyEmail(token);
  }, [token, userEmail]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      // API Call: GET /api/auth/verify-email/<token>/
      const response = await api.get<{ message?: string; detail?: string }>(
        `/api/auth/verify-email/${verificationToken}/`
      );

      setStatus('success');
      setMessage(response.message || 'Your email has been verified successfully!');

      // Auto-redirect to login modal after 3 seconds
      setTimeout(() => {
        navigate('/', { state: { openAuth: true, authView: 'signin' } });
      }, 3000);
    } catch (error: any) {
      console.error('Verification error:', error);
      
      if (error.response?.data?.detail) {
        setMessage(error.response.data.detail);
      } else if (error.response?.data?.token) {
        setMessage('Invalid or expired verification token.');
      } else if (error.code === 'ERR_NETWORK') {
        setMessage('Network error. Please check your connection and try again.');
      } else {
        setMessage('Failed to verify email. The link may have expired.');
      }
      
      setStatus('error');
    }
  };

  const handleResendEmail = async () => {
    if (!email) return;

    setIsResending(true);
    setMessage('Resending verification email...');

    try {
      await api.post('/api/auth/resend-verification/', { email });
      setStatus('invalid');
      setMessage('A new verification email has been sent. Please check your inbox.');
    } catch (error) {
      setIsResending(false);
      setStatus('error');
      setMessage('Failed to resend verification email. Please try again later.');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying your email...</h2>
            <p className="text-gray-600">Please wait while we verify your email address.</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <p className="text-sm text-gray-500 mb-6">Redirecting you to login...</p>
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
            >
              Continue to Login
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        );

      case 'error':
      case 'invalid':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {status === 'invalid' ? 'Invalid Link' : 'Verification Failed'}
            </h2>
            <p className="text-gray-600 mb-6">{message}</p>
            
            {email && (
              <button
                onClick={handleResendEmail}
                disabled={isResending}
                className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50 transition-colors mb-4"
              >
                <RefreshCw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                Resend Verification Email
              </button>
            )}
            
            <div className="mt-4">
              <Link 
                to="/" 
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Back to Home
              </Link>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Verify Email | AdsWorld</title>
        <meta name="description" content="Verify your email to activate your account" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">AdsWorld</h1>
            <p className="text-gray-500">Buy & Sell Locally</p>
          </div>

          {/* Content */}
          {renderContent()}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              Need help?{' '}
              <a href="mailto:support@bazaar.pk" className="text-primary-600 hover:text-primary-700 font-medium">
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
